import upperFirst from 'lodash/upperFirst'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { TableCellProps } from 'react-virtualized'

import { Trip, Vehicle_TypeEnum } from 'src/api/gen/api'
import { VEHICLES_CATEGORY_ICON_MAP } from 'src/commons/constants'
import { enumsTranslationKeys } from 'src/commons/i18nEnums'
import { CellLink } from 'src/components/Table/Cells'
import { FullExportableTable, ISearchBar } from 'src/components/Table/Templates/FullExportableTable'
import { dateCellRenderer, iconCellRenderer, providerCellRenderer } from 'src/components/Table/utils'
import { useAgencySettingsDistanceUnit, useAgencySettingsSpeedUnit } from 'src/layout/AgencySettings'
import { useProviders, useProviderUser } from 'src/layout/hooks'

import { formatDuration } from '../common'

interface IProps {
  className?: string
  data: Trip[]
  isLoading?: boolean
  onClickExport?: (event: React.MouseEvent<HTMLElement>) => any
  // Search the trip_id, the uuid and the VIN
  searchBar?: ISearchBar
  totalCount: number
}

const TripsTable = (props: IProps) => {
  const { t } = useTranslation()
  const providers = useProviders()
  const { activeProviders } = useProviders()
  const isProviderUser = useProviderUser()

  const { symbol: distanceUnitSymbol } = useAgencySettingsDistanceUnit()
  const { symbol: speedUnitSymbol } = useAgencySettingsSpeedUnit()

  const columns = [
    {
      label: upperFirst(t('trips.id')),
      width: 100,
      dataKey: 'id',
    },
    {
      label: upperFirst(t('trips.vin')),
      width: 70,
      dataKey: 'vehicle_id',
      cellRenderer: (cellProps: TableCellProps) => {
        const trip = cellProps.rowData as any
        return <CellLink url={`/vehicles/${trip.device_id}`}>{trip.vehicle_id}</CellLink>
      },
    },
    {
      label: upperFirst(t('vehicles.provider')),
      width: 110,
      dataKey: 'provider_id',
      cellRenderer: (cellProps: TableCellProps) => providerCellRenderer(cellProps, providers.activeProviders),
    },
    {
      label: upperFirst(t('vehicles.category')),
      width: 120,
      dataKey: 'vehicle_type',
      cellRenderer: (cellProps: TableCellProps) =>
        iconCellRenderer(
          cellProps,
          (cat: Vehicle_TypeEnum) => upperFirst(t(enumsTranslationKeys.DeviceType[cat])),
          (cat: Vehicle_TypeEnum) => VEHICLES_CATEGORY_ICON_MAP[cat]
        ),
    },
    {
      label: upperFirst(t('trips.startTime')),
      width: 120,
      dataKey: 'start_time',
      cellRenderer: dateCellRenderer,
    },
    {
      label: upperFirst(t('trips.endTime')),
      width: 120,
      dataKey: 'end_time',
      cellRenderer: dateCellRenderer,
    },
    {
      label: upperFirst(t('trips.duration')),
      width: 120,
      dataKey: 'duration',
      cellRenderer: (cellProps: TableCellProps) => {
        const trip = cellProps.rowData as any
        if (trip.duration == null) {
          return 'No Data'
        }
        return formatDuration(trip.duration / 60)
      },
    },
  ]
  columns.push({
    label: upperFirst(t('trips.distance')),
    width: 120,
    dataKey: 'distance',
    cellRenderer: (cellProps: TableCellProps) => {
      const trip = cellProps.rowData as any
      const distance = trip.distance
      if (distance === null || distance === undefined) {
        return 'No Data'
      }
      return `${distance} ${distanceUnitSymbol}`
    },
  })
  columns.push({
    label: upperFirst(t('trips.mediumSpeed')),
    width: 120,
    dataKey: 'speed',
    cellRenderer: (cellProps: TableCellProps) => {
      const trip = cellProps.rowData as any
      const speed = trip.speed
      if (speed === null || speed === undefined) {
        return 'No Data'
      }
      return `${speed} ${speedUnitSymbol}`
    },
  })

  if (isProviderUser && activeProviders.length > 2) {
    // we remove the provider
    columns.splice(2, 1)
  }

  return (
    <FullExportableTable
      data={props.data}
      columns={columns}
      className={props.className}
      noDataMessage={t('trips.table.noData')}
      totalCount={props.totalCount}
      onClickExport={props.onClickExport}
      isLoading={props.isLoading}
      searchBar={props.searchBar}
    />
  )
}

export default TripsTable
